import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Users, Clock, TrendingUp } from "lucide-react";
import { exportToCSV } from "./ReportExport";

export default function WorkloadReport({ users, tasks, timeEntries, startDate, endDate }) {
  const periodTimeEntries = timeEntries.filter(e => {
    const entryDate = new Date(e.start_time);
    return entryDate >= startDate && entryDate <= endDate;
  });

  const periodTasks = tasks.filter(t => {
    if (!t.due_date) return false;
    const dueDate = new Date(t.due_date);
    return dueDate >= startDate && dueDate <= endDate;
  });

  const userWorkload = users.map(user => {
    const userTimeEntries = periodTimeEntries.filter(e => e.user_email === user.email);
    const userTasks = periodTasks.filter(t => t.assigned_to === user.email);
    
    const actualHours = userTimeEntries.reduce((sum, e) => sum + (e.duration_minutes || 0) / 60, 0);
    const estimatedHours = userTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);
    const totalHours = actualHours + estimatedHours;
    
    const completedTasks = userTasks.filter(t => t.status === "completed").length;
    const activeTasks = userTasks.filter(t => t.status === "in_progress").length;
    
    return {
      name: user.full_name,
      email: user.email,
      actualHours: actualHours.toFixed(1),
      estimatedHours: estimatedHours.toFixed(1),
      totalHours: totalHours.toFixed(1),
      totalTasks: userTasks.length,
      completedTasks,
      activeTasks,
      capacity: user.weekly_capacity || 40,
    };
  }).filter(u => u.totalHours > 0);

  const stats = {
    totalUsers: userWorkload.length,
    totalHours: userWorkload.reduce((sum, u) => sum + parseFloat(u.totalHours), 0),
    totalTasks: userWorkload.reduce((sum, u) => sum + u.totalTasks, 0),
    avgHoursPerUser: userWorkload.length > 0 
      ? userWorkload.reduce((sum, u) => sum + parseFloat(u.totalHours), 0) / userWorkload.length 
      : 0,
  };

  const handleExport = () => {
    const exportData = userWorkload.map(u => ({
      "Navn": u.name,
      "E-post": u.email,
      "Utførte timer": u.actualHours,
      "Estimerte timer": u.estimatedHours,
      "Totalt timer": u.totalHours,
      "Kapasitet": u.capacity,
      "Oppgaver totalt": u.totalTasks,
      "Fullførte oppgaver": u.completedTasks,
      "Aktive oppgaver": u.activeTasks,
    }));
    exportToCSV(exportData, `arbeidsmengde_${new Date().toISOString().split('T')[0]}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Arbeidsmengde per bruker</h3>
        <Button onClick={handleExport} variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          Eksporter til CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-zinc-900" />
            <p className="text-xs text-slate-500">Aktive brukere</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats.totalUsers}</p>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-emerald-600" />
            <p className="text-xs text-slate-500">Totale timer</p>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{stats.totalHours.toFixed(0)}h</p>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-amber-600" />
            <p className="text-xs text-slate-500">Snitt timer/bruker</p>
          </div>
          <p className="text-2xl font-bold text-amber-600">{stats.avgHoursPerUser.toFixed(1)}h</p>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-zinc-900" />
            <p className="text-xs text-slate-500">Totale oppgaver</p>
          </div>
          <p className="text-2xl font-bold text-zinc-900">{stats.totalTasks}</p>
        </Card>
      </div>

      <Card className="bg-white border-0 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Bruker</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Utført</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Planlagt</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Totalt</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Kapasitet</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Oppgaver</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Fullført</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Aktive</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {userWorkload.map((user, idx) => {
                const utilization = (parseFloat(user.totalHours) / user.capacity) * 100;
                return (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-emerald-600 text-right font-medium">
                      {user.actualHours}h
                    </td>
                    <td className="px-4 py-3 text-sm text-amber-600 text-right font-medium">
                      {user.estimatedHours}h
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900 text-right font-semibold">
                      {user.totalHours}h
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">
                      {user.capacity}h
                      <span className={`ml-2 text-xs ${utilization > 100 ? 'text-rose-600' : 'text-slate-500'}`}>
                        ({utilization.toFixed(0)}%)
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900 text-right">{user.totalTasks}</td>
                    <td className="px-4 py-3 text-sm text-emerald-600 text-right">{user.completedTasks}</td>
                    <td className="px-4 py-3 text-sm text-zinc-900 text-right">{user.activeTasks}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}